/**
 * Editing commands behind the editor toolbar and the Insert / Format menus. Each takes the
 * EditorView and returns false when it did nothing (e.g. the file is read-only).
 */
import { startCompletion } from "@codemirror/autocomplete";
import { indentLess, indentMore } from "@codemirror/commands";
import { EditorSelection, type EditorState, type SelectionRange } from "@codemirror/state";
import type { EditorView } from "@codemirror/view";
import { collabOptions, redoCommand, undoCommand } from "./collab";

// ---------------------------------------------------------------- undo

/** Undo through the shared undo manager, so undoing a tracked edit also removes its suggestion. */
export const undo = (view: EditorView) => !view.state.readOnly && undoCommand(view);
export const redo = (view: EditorView) => !view.state.readOnly && redoCommand(view);
export const canUndo = (state: EditorState) => (state.facet(collabOptions)?.undoManager.undoStack.length ?? 0) > 0;
export const canRedo = (state: EditorState) => (state.facet(collabOptions)?.undoManager.redoStack.length ?? 0) > 0;

// ---------------------------------------------------------------- bold / italic

const LOOKBEHIND = 5000;

/** The innermost \cmd{...} (one of `cmds`) around [from, to]: where the command starts, its content, and its end. */
export function enclosingCommand(state: EditorState, from: number, to: number, cmds: string[]) {
  const start = Math.max(0, from - LOOKBEHIND);
  const before = state.sliceDoc(start, from);
  let depth = 0;
  for (let i = before.length - 1; i >= 0; i--) {
    const ch = before[i];
    if (before[i - 1] === "\\") continue;
    if (ch === "}") depth++;
    else if (ch === "{") {
      if (depth > 0) {
        depth--;
        continue;
      }
      const m = /\\([a-zA-Z]+)\s*$/.exec(before.slice(Math.max(0, i - 40), i));
      const open = start + i;
      const close = matchBrace(state, open);
      if (close === null || close < to) continue;
      if (m && cmds.includes(m[1])) return { cmd: m[1], from: open - m[0].length, contentFrom: open + 1, contentTo: close, to: close + 1 };
    }
  }
  return null;
}

/** Position of the `}` closing the `{` at `open`, or null. */
export function matchBrace(state: EditorState, open: number, limit = 20_000): number | null {
  const text = state.sliceDoc(open, Math.min(state.doc.length, open + limit));
  let depth = 0;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === "\\") {
      i++;
      continue;
    }
    if (ch === "{") depth++;
    else if (ch === "}" && --depth === 0) return open + i;
  }
  return null;
}

function toggleCommand(cmd: string, alsoMatches: string[] = []) {
  return (view: EditorView): boolean => {
    if (view.state.readOnly) return false;
    const names = [cmd, ...alsoMatches];
    view.dispatch(
      view.state.changeByRange((range) => {
        const hit = enclosingCommand(view.state, range.from, range.to, names);
        if (hit) {
          const shift = hit.contentFrom - hit.from;
          return {
            changes: [
              { from: hit.from, to: hit.contentFrom },
              { from: hit.contentTo, to: hit.to },
            ],
            range: EditorSelection.range(range.anchor - shift, range.head - shift),
          };
        }
        const text = view.state.sliceDoc(range.from, range.to);
        const open = `\\${cmd}{`;
        return {
          changes: { from: range.from, to: range.to, insert: `${open}${text}}` },
          range: EditorSelection.range(range.from + open.length, range.from + open.length + text.length),
        };
      }),
      { userEvent: "input.format", scrollIntoView: true },
    );
    view.focus();
    return true;
  };
}

export const toggleBold = toggleCommand("textbf");
export const toggleItalic = toggleCommand("textit", ["emph"]);
export const isBold = (state: EditorState) => !!enclosingCommand(state, state.selection.main.from, state.selection.main.to, ["textbf"]);
export const isItalic = (state: EditorState) => !!enclosingCommand(state, state.selection.main.from, state.selection.main.to, ["textit", "emph"]);

// ---------------------------------------------------------------- headings

export type HeadingLevel = "normal" | "part" | "chapter" | "section" | "subsection" | "subsubsection" | "paragraph" | "subparagraph";

export const HEADINGS: Array<{ level: HeadingLevel; label: string }> = [
  { level: "normal", label: "Normal text" },
  { level: "part", label: "Part" },
  { level: "chapter", label: "Chapter" },
  { level: "section", label: "Section" },
  { level: "subsection", label: "Subsection" },
  { level: "subsubsection", label: "Subsubsection" },
  { level: "paragraph", label: "Paragraph" },
  { level: "subparagraph", label: "Subparagraph" },
];

const HEADING_RE = /^(\s*)\\(part|chapter|section|subsection|subsubsection|paragraph|subparagraph)(\*?)\s*(?:\[[^\]]*\])?\s*\{/;

export function headingAt(state: EditorState): HeadingLevel {
  const m = HEADING_RE.exec(state.doc.lineAt(state.selection.main.head).text);
  return m ? (m[2] as HeadingLevel) : "normal";
}

/** Make the line with the cursor a heading of `level`, or plain text again. */
export function setHeading(view: EditorView, level: HeadingLevel): boolean {
  const { state } = view;
  if (state.readOnly) return false;
  const line = state.doc.lineAt(state.selection.main.head);
  const m = HEADING_RE.exec(line.text);
  if (m) {
    const cmdFrom = line.from + m[1].length + 1;
    const cmdTo = cmdFrom + m[2].length;
    if (level !== "normal") {
      view.dispatch({ changes: { from: cmdFrom, to: cmdTo, insert: level }, userEvent: "input.format" });
    } else {
      // Keep the title, drop the command and its braces.
      const open = line.from + m[0].length - 1;
      const close = matchBrace(state, open);
      if (close === null) return false;
      view.dispatch({
        changes: [
          { from: line.from + m[1].length, to: open + 1 },
          { from: close, to: close + 1 },
        ],
        userEvent: "input.format",
      });
    }
  } else if (level !== "normal") {
    const indent = /^\s*/.exec(line.text)![0];
    const text = line.text.slice(indent.length).trimEnd();
    const open = `\\${level}{`;
    view.dispatch({
      changes: { from: line.from + indent.length, to: line.from + indent.length + text.length, insert: `${open}${text}}` },
      selection: { anchor: line.from + indent.length + open.length + text.length },
      userEvent: "input.format",
    });
  }
  view.focus();
  return true;
}

// ---------------------------------------------------------------- insertions

/** Replace each selection with before + selected text + after, leaving the selected text (or the cursor) between them. */
function surround(view: EditorView, before: string, after: string, then?: (view: EditorView) => void): boolean {
  if (view.state.readOnly) return false;
  view.dispatch(
    view.state.changeByRange((range) => {
      const text = view.state.sliceDoc(range.from, range.to);
      return {
        changes: { from: range.from, to: range.to, insert: before + text + after },
        range: EditorSelection.range(range.from + before.length, range.from + before.length + text.length),
      };
    }),
    { userEvent: "input", scrollIntoView: true },
  );
  view.focus();
  then?.(view);
  return true;
}

/**
 * Insert lines as a block of their own: on a blank line they replace it, otherwise they go
 * on a new line below. `|` in the lines marks where the cursor goes.
 */
function insertBlock(view: EditorView, lines: string[]): boolean {
  const { state } = view;
  if (state.readOnly) return false;
  const line = state.doc.lineAt(state.selection.main.head);
  const indent = /^\s*/.exec(line.text)![0];
  const blank = !line.text.trim();
  const body = lines.map((l, i) => (i === 0 && blank ? l : indent + l)).join("\n");
  const at = blank ? line.from + indent.length : line.to;
  const insert = (blank ? "" : "\n") + body.replace("|", "");
  const cursor = at + (blank ? 0 : 1) + body.indexOf("|");
  view.dispatch({ changes: { from: at, to: blank ? line.to : at, insert }, selection: { anchor: cursor }, userEvent: "input", scrollIntoView: true });
  view.focus();
  return true;
}

export const insertInlineMath = (view: EditorView) => surround(view, "\\(", "\\)");

export function insertDisplayMath(view: EditorView): boolean {
  const { state } = view;
  const sel = state.selection.main;
  if (sel.empty && !state.doc.lineAt(sel.head).text.trim()) return insertBlock(view, ["\\[", "  |", "\\]"]);
  return surround(view, "\\[ ", " \\]");
}

export function insertLink(view: EditorView): boolean {
  const sel = view.state.selection.main;
  const text = view.state.sliceDoc(sel.from, sel.to);
  if (/^(https?:\/\/|www\.)\S+$/.test(text)) return surround(view, "\\url{", "}");
  if (view.state.readOnly) return false;
  // Cursor in the URL; the selected text becomes the link text.
  const insert = `\\href{}{${text}}`;
  view.dispatch({ changes: { from: sel.from, to: sel.to, insert }, selection: { anchor: sel.from + 6 }, userEvent: "input", scrollIntoView: true });
  view.focus();
  return true;
}

function insertWithCompletion(view: EditorView, cmd: string): boolean {
  const sel = view.state.selection.main;
  if (view.state.readOnly) return false;
  const insert = `\\${cmd}{}`;
  view.dispatch({ changes: { from: sel.from, to: sel.to, insert }, selection: { anchor: sel.from + insert.length - 1 }, userEvent: "input", scrollIntoView: true });
  view.focus();
  startCompletion(view);
  return true;
}

export const insertCrossRef = (view: EditorView) => insertWithCompletion(view, "ref");
export const insertCitation = (view: EditorView) => insertWithCompletion(view, "cite");

/** A figure environment, with the image already filled in when `path` is given. */
export function insertFigure(view: EditorView, path = ""): boolean {
  const label = path.replace(/^.*\//, "").replace(/\.[^.]*$/, "");
  const ok = insertBlock(view, [
    "\\begin{figure}[htbp]",
    "  \\centering",
    path ? `  \\includegraphics[width=0.8\\linewidth]{${path}}` : "  \\includegraphics[width=0.8\\linewidth]{|}",
    path ? "  \\caption{|}" : "  \\caption{}",
    `  \\label{fig:${label}}`,
    "\\end{figure}",
  ]);
  if (ok && !path) startCompletion(view);
  return ok;
}

export function insertTable(view: EditorView, rows: number, cols: number): boolean {
  rows = Math.max(1, Math.min(20, rows));
  cols = Math.max(1, Math.min(20, cols));
  const row = (first: boolean) => "    " + Array.from({ length: cols }, (_, i) => (first && i === 0 ? "|" : "")).join(" & ") + " \\\\";
  return insertBlock(view, [
    "\\begin{table}[htbp]",
    "  \\centering",
    "  \\caption{}",
    "  \\label{tab:}",
    `  \\begin{tabular}{${"l".repeat(cols)}}`,
    "    \\hline",
    row(true),
    ...(rows > 1 ? ["    \\hline", ...Array.from({ length: rows - 1 }, () => row(false))] : []),
    "    \\hline",
    "  \\end{tabular}",
    "\\end{table}",
  ]);
}

function insertList(env: "itemize" | "enumerate") {
  return (view: EditorView): boolean => {
    const { state } = view;
    if (state.readOnly) return false;
    const sel = state.selection.main;
    const first = state.doc.lineAt(sel.from);
    const last = state.doc.lineAt(sel.to);
    // Selected lines become the items.
    if (!sel.empty && (first.number !== last.number || first.text.trim())) {
      const indent = /^\s*/.exec(first.text)![0];
      const items: string[] = [];
      for (let n = first.number; n <= last.number; n++) {
        const t = state.doc.line(n).text.trim();
        if (t) items.push(`${indent}  \\item ${t.replace(/^\\item\s*/, "")}`);
      }
      const insert = [`${indent}\\begin{${env}}`, ...items, `${indent}\\end{${env}}`].join("\n");
      view.dispatch({ changes: { from: first.from, to: last.to, insert }, selection: { anchor: first.from + insert.length }, userEvent: "input" });
      view.focus();
      return true;
    }
    return insertBlock(view, [`\\begin{${env}}`, "  \\item |", `\\end{${env}}`]);
  };
}

export const insertBulletList = insertList("itemize");
export const insertNumberedList = insertList("enumerate");

const LIST_ENVS = /\\(begin|end)\{(itemize|enumerate|description)\}/g;

/** The list environments open at `pos`, innermost last. */
function listStack(state: EditorState, pos: number): string[] {
  const stack: string[] = [];
  for (const m of state.sliceDoc(Math.max(0, pos - 20_000), pos).matchAll(LIST_ENVS)) {
    if (m[1] === "begin") stack.push(m[2]);
    else if (stack[stack.length - 1] === m[2]) stack.pop();
  }
  return stack;
}

/** Nest the item on the cursor's line one level deeper (or indent, outside lists). */
export function indentList(view: EditorView): boolean {
  const { state } = view;
  if (state.readOnly) return false;
  const line = state.doc.lineAt(state.selection.main.head);
  const stack = listStack(state, line.from);
  if (!/^\s*\\item\b/.test(line.text) || !stack.length || line.number === 1) return indentMore(view);
  const env = stack[stack.length - 1];
  const indent = /^\s*/.exec(line.text)![0];
  const item = line.text.slice(indent.length);
  const prev = state.doc.line(line.number - 1);
  // Keep the cursor where it was within the item's text.
  const within = Math.max(0, state.selection.main.head - line.from - indent.length);
  // Join a sub-list that ends just above; otherwise start a new one.
  if (new RegExp(`^${indent}\\s+\\\\end\\{${env}\\}\\s*$`).test(prev.text)) {
    const inner = /^\s*/.exec(prev.text)![0];
    const lead = `${inner}  `;
    view.dispatch({
      changes: { from: prev.from, to: line.to, insert: `${lead}${item}\n${inner}\\end{${env}}` },
      selection: { anchor: prev.from + lead.length + within },
      userEvent: "input.indent",
    });
  } else {
    const begin = `${indent}  \\begin{${env}}\n`;
    const lead = `${indent}    `;
    view.dispatch({
      changes: { from: line.from, to: line.to, insert: `${begin}${lead}${item}\n${indent}  \\end{${env}}` },
      selection: { anchor: line.from + begin.length + lead.length + within },
      userEvent: "input.indent",
    });
  }
  view.focus();
  return true;
}

/** Move the item on the cursor's line out of its sub-list (or outdent, outside sub-lists). */
export function outdentList(view: EditorView): boolean {
  const { state } = view;
  if (state.readOnly) return false;
  const line = state.doc.lineAt(state.selection.main.head);
  const stack = listStack(state, line.from);
  if (!/^\s*\\item\b/.test(line.text) || stack.length < 2 || line.number === state.doc.lines) return indentLess(view);
  const env = stack[stack.length - 1];
  const next = state.doc.line(line.number + 1);
  // Only the last item of a sub-list can move out without splitting it.
  if (!new RegExp(`^\\s*\\\\end\\{${env}\\}\\s*$`).test(next.text)) return indentLess(view);
  // Line up with the items of the list this one sits in.
  let outer = /^\s*/.exec(next.text)![0].slice(2);
  for (let n = line.number - 1; n >= 1; n--) {
    const t = state.doc.line(n).text;
    const m = /^(\s*)\\begin\{(itemize|enumerate|description)\}/.exec(t);
    if (m && m[1].length < /^\s*/.exec(next.text)![0].length) {
      outer = `${m[1]}  `;
      break;
    }
  }
  const item = line.text.trimStart();
  const prev = line.number > 1 ? state.doc.line(line.number - 1) : null;
  const onlyItem = prev && new RegExp(`^\\s*\\\\begin\\{${env}\\}\\s*$`).test(prev.text);
  const from = onlyItem ? prev!.from : line.from;
  const insert = onlyItem ? `${outer}${item}` : `${next.text}\n${outer}${item}`;
  view.dispatch({ changes: { from, to: next.to, insert }, selection: { anchor: from + insert.length }, userEvent: "input.indent" });
  view.focus();
  return true;
}

// ---------------------------------------------------------------- symbols

const MATH_ENVS = "equation|align|gather|multline|flalign|alignat|eqnarray|displaymath|math";

/** Whether `pos` is inside maths: $...$, \(...\), \[...\] or an equation-like environment. */
export function inMath(state: EditorState, pos: number): boolean {
  const start = Math.max(0, pos - 3000);
  const text = state
    .sliceDoc(start, pos)
    // Comments and escaped dollars don't count.
    .replace(/(^|[^\\])%.*$/gm, "$1")
    .replace(/\\\$/g, "");
  const last = (re: RegExp) => {
    let at = -1;
    for (const m of text.matchAll(re)) at = m.index!;
    return at;
  };
  if (last(/\\\(/g) > last(/\\\)/g)) return true;
  if (last(/\\\[/g) > last(/\\\]/g)) return true;
  if (last(new RegExp(`\\\\begin\\{(?:${MATH_ENVS})\\*?\\}`, "g")) > last(new RegExp(`\\\\end\\{(?:${MATH_ENVS})\\*?\\}`, "g"))) return true;
  // Only the current paragraph for dollars, so a stray $ far above doesn't flip everything.
  const para = text.slice(text.lastIndexOf("\n\n") + 1).replace(/\$\$/g, "");
  return (para.match(/\$/g)?.length ?? 0) % 2 === 1;
}

/** Insert a symbol's command, wrapped in \( \) if it needs maths and the cursor isn't in maths already. */
export function insertSymbol(view: EditorView, latex: string, math = true): boolean {
  const { state } = view;
  if (state.readOnly) return false;
  view.dispatch(
    state.changeByRange((range: SelectionRange) => {
      const next = state.sliceDoc(range.to, range.to + 1);
      // `\alpha` followed by a letter would read as one longer command.
      const spaced = /[a-zA-Z]$/.test(latex) && /[a-zA-Z]/.test(next) ? `${latex} ` : latex;
      const insert = math && !inMath(state, range.from) ? `\\(${spaced}\\)` : spaced;
      return { changes: { from: range.from, to: range.to, insert }, range: EditorSelection.cursor(range.from + insert.length) };
    }),
    { userEvent: "input", scrollIntoView: true },
  );
  view.focus();
  return true;
}

/** Select the word at the cursor if nothing is selected (e.g. before adding a comment). */
export function selectWordIfEmpty(view: EditorView): { from: number; to: number } | null {
  const sel = view.state.selection.main;
  if (!sel.empty) return { from: sel.from, to: sel.to };
  const word = view.state.wordAt(sel.head);
  if (!word) return null;
  view.dispatch({ selection: { anchor: word.from, head: word.to } });
  return { from: word.from, to: word.to };
}
